import type { ReactNode } from 'react';
import { cx } from '../cx';
import { NARROW_ONLY, WIDE_ONLY, type ChartLayout } from './layout';
import { TICK_TEXT } from './scale';

const PLOT_H: Record<ChartLayout, string> = { auto: 'h-44 @md:h-52', narrow: 'h-44', wide: 'h-52' };

/**
 * The concentration curve: for each share of employers, busiest first, the share of all ads they
 * list. Returns up to 101 points as [employerShare, adShare], both 0 to 1, from (0, 0) to (1, 1).
 */
export function concentrationPoints(adsPerEmployer: number[]): Array<[number, number]> {
  const counts = adsPerEmployer.filter((c) => c > 0).sort((a, b) => b - a);
  const total = counts.reduce((s, c) => s + c, 0);
  if (!total)
    return [
      [0, 0],
      [1, 1],
    ];
  const out: Array<[number, number]> = [];
  const n = counts.length;
  const samples = Math.min(100, n);
  for (let k = 0; k <= samples; k++) out.push([k / samples, concentrationAt(counts, k / samples)]);
  return out;
}

/** The share of ads (0 to 1) listed by the busiest `employerShare` of employers. */
export function concentrationAt(adsPerEmployer: number[], employerShare: number): number {
  const counts = adsPerEmployer.filter((c) => c > 0).sort((a, b) => b - a);
  const total = counts.reduce((s, c) => s + c, 0);
  if (!total) return 0;
  const exact = employerShare * counts.length;
  const whole = Math.floor(exact);
  let sum = counts.slice(0, whole).reduce((s, c) => s + c, 0);
  if (whole < counts.length) sum += (exact - whole) * counts[whole];
  return sum / total;
}

export type ConcentrationCurveProps = {
  /** Ads per employer (agencies and group boards counted as one employer each). */
  adsPerEmployer: number[];
  /** The one labelled point that carries the story, with its words computed by `concentrationAt`. */
  reading: { employerShare: number; label: ReactNode };
  /** Names the dashed line ("Dashed: every employer with the same number of ads"). */
  equalityLabel: ReactNode;
  formatShare: (share: number) => string;
  /** Says what each axis is ("Share of ads", "Share of employers, busiest first"). */
  axisLabels: { ads: ReactNode; employers: ReactNode };
  /** `auto` (default) switches to the narrow form in a narrow container: a shorter plot, the reading and the dashed line's name under it. */
  layout?: ChartLayout;
  /** One sentence with the reading, for screen readers. */
  description: string;
  className?: string;
};

/**
 * How concentrated hiring is: the share of ads listed by the busiest share of employers, against
 * the dashed line where every employer lists the same. One labelled point tells the story ("the
 * busiest 10% of employers list half the ads"); no Gini coefficient. Try a top-10 BarChart first.
 */
export function ConcentrationCurve({
  adsPerEmployer,
  reading,
  equalityLabel,
  formatShare,
  axisLabels,
  layout = 'auto',
  description,
  className,
}: ConcentrationCurveProps) {
  const pts = concentrationPoints(adsPerEmployer);
  const at = concentrationAt(adsPerEmployer, reading.employerShare);
  const ticks = [0, 0.5, 1];
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-1', className)}>
      <span className={TICK_TEXT} aria-hidden="true">
        {axisLabels.ads}
      </span>
      <div className="flex gap-2">
        <div className={cx('relative w-10 shrink-0', PLOT_H[layout])} aria-hidden="true">
          {ticks.map((t) => (
            <span
              key={t}
              className={cx('absolute right-0 translate-y-1/2', TICK_TEXT)}
              style={{ bottom: `${t * 100}%` }}
            >
              {formatShare(t)}
            </span>
          ))}
        </div>
        <div className={cx('relative mr-4 flex-1', PLOT_H[layout])}>
          {ticks.map((t) => (
            <span
              key={t}
              className="absolute inset-x-0 border-t border-jc-chart-grid"
              style={{ bottom: `${t * 100}%` }}
            />
          ))}
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="absolute inset-0 size-full overflow-visible motion-safe:animate-jc-fade"
            aria-hidden="true"
          >
            <line
              x1="0"
              y1="100"
              x2="100"
              y2="0"
              vectorEffect="non-scaling-stroke"
              strokeWidth={1.5}
              strokeDasharray="4 4"
              className="stroke-jc-chart-context"
            />
            <polyline
              points={pts.map(([e, a]) => `${e * 100},${100 - a * 100}`).join(' ')}
              fill="none"
              vectorEffect="non-scaling-stroke"
              strokeWidth={2.5}
              strokeLinejoin="round"
              className="stroke-jc-ink"
            />
          </svg>
          <span
            className={cx(
              'absolute right-0 bottom-1/3 max-w-1/2 text-right text-xs text-jc-ink-secondary',
              WIDE_ONLY[layout],
            )}
            aria-hidden="true"
          >
            {equalityLabel}
          </span>
          <span
            aria-hidden="true"
            className="absolute z-10"
            style={{ left: `${reading.employerShare * 100}%`, top: `${100 - at * 100}%` }}
          >
            <span className="absolute size-3 -translate-1/2 rounded-full bg-jc-chart-2 ring-2 ring-jc-ink" />
            <span
              className={cx(
                'absolute top-0 left-4 w-56 -translate-y-1/2 text-xs font-semibold text-jc-ink',
                WIDE_ONLY[layout],
              )}
            >
              {reading.label}
            </span>
          </span>
        </div>
      </div>
      <div className="relative mr-4 ml-12 h-4" aria-hidden="true">
        {ticks.map((t) => (
          <span
            key={t}
            className={cx('absolute -translate-x-1/2 whitespace-nowrap', TICK_TEXT)}
            style={{ left: `${t * 100}%` }}
          >
            {formatShare(t)}
          </span>
        ))}
      </div>
      <span className={cx('ml-12 text-center', TICK_TEXT)} aria-hidden="true">
        {axisLabels.employers}
      </span>
      <div className={cx('flex flex-col gap-1 pt-1', NARROW_ONLY[layout])} aria-hidden="true">
        <p className="flex items-center gap-2 type-meta font-semibold text-jc-ink">
          <span className="size-3 shrink-0 rounded-full bg-jc-chart-2 ring-2 ring-jc-ink" />
          {reading.label}
        </p>
        <p className="flex items-center gap-2 type-meta text-jc-ink-secondary">
          <span className="w-4 shrink-0 border-t-2 border-dashed border-jc-chart-context" />
          {equalityLabel}
        </p>
      </div>
    </div>
  );
}
